import { Spinner } from '@blueprintjs/core'
import { Popover } from '../ui/popover'
import classNames from 'classnames'
import React, { useCallback, useEffect, useState } from 'react'
import { useScreenshotMode } from '../../hooks/useScreenshotMode'
import { adManagerPerformanceGet } from '../../util/adManagerPerformanceApi'
import { number } from '../../util/formatters'
import { AmazonLink } from '../amazon_link'

const ASINDisplayLimit = 20

export const fetchTargetedProductAsins = (item) => {
    const filter = item.ad_group_id
        ? { ad_group_id: item.ad_group_id }
        : item.campaign_id
          ? { campaign_id: item.campaign_id }
          : null

    if (!filter) {
        return null
    }

    return adManagerPerformanceGet('api/v5/product-ads', { perPage: 300, filter }, true).then(
        (res) =>
            Array.from(
                new Set(
                    res.data.data
                        .map((productAd) => productAd.asin)
                        .filter((asin) => typeof asin === 'string' && asin.length > 0),
                ),
            ),
    )
}

export const TargetedProducts = ({ item, className = '' }) => {
    const [products, setProducts] = useState([])
    const [loaded, setLoaded] = useState(false)
    const [loading, setLoading] = useState(false)
    const screenshotMode = useScreenshotMode()

    useEffect(() => {
        setProducts([])
        setLoading(false)
        setLoaded(false)
    }, [item])

    const loadProducts = useCallback(() => {
        if (loaded) {
            return
        }

        const request = fetchTargetedProductAsins(item)
        if (!request) {
            console.error('Cannot resolve type')
            return
        }

        setLoading(true)

        request
            .then((asins) => {
                setLoading(false)
                setLoaded(true)
                setProducts(asins)
            })
            .catch((res) => {
                setLoading(false)
                setLoaded(true)
                console.log('res is', res)
            })
    }, [item, loaded])

    return (
        <TargetedProductsPure
            className={className}
            loading={loading}
            loaded={loaded}
            loadProducts={loadProducts}
            products={products}
            item={item}
            screenshotMode={screenshotMode}
        />
    )
}

class TargetedProductsPure extends React.PureComponent {
    render() {
        const { loading, loaded, className, loadProducts, products, item, screenshotMode } =
            this.props

        const count =
            item?.product_ads_count ||
            item?.ad_group?.product_ads_count ||
            item?.campaign?.product_ads_count
        if (count !== undefined) {
            if (count === 0) {
                return null
            }

            return (
                <Popover
                    position="bottom"
                    interactionKind="hover"
                    onOpening={loadProducts}
                    className={classNames(className)}
                    content={
                        <div
                            className={classNames('p-2', { blur: screenshotMode })}
                            style={{ maxWidth: '350px' }}
                        >
                            {loading && <Spinner />}
                            {loaded && <AsinLinks asins={products} count={count} />}
                        </div>
                    }
                >
                    <div>
                        {number(count)}&nbsp;Product{count > 1 ? 's' : ''}
                    </div>
                </Popover>
            )
        }

        let asins = getAsins(item)

        const len = asins.length
        if (len === 0) {
            return null
        } else if (len === 1) {
            return <AmazonLink className={className} asin={asins[0]} />
        }

        const extra = asins.length - ASINDisplayLimit
        if (asins.length > ASINDisplayLimit) {
            asins = asins.slice(0, ASINDisplayLimit)
        }

        let links = asins.map((asin) => {
            return <AmazonLink key={asin} asin={asin} />
        })
        if (extra > 0) {
            links.push('and ' + extra + ' others')
        }
        links = links.reduce((prev, curr) => [prev, ', ', curr])

        return (
            <Popover
                position="bottom"
                content={
                    <div className={classNames('p-2', { blur: screenshotMode })}>
                        {/* Create a list of AmazonLinks, then separate them by a comma*/}
                        {links}
                    </div>
                }
                interactionKind="hover"
            >
                <span className={classNames('pointer', className)}>{len} Products</span>
            </Popover>
        )
    }
}

const AsinLinks = ({ asins, count }) => {
    if (asins.length === 0) {
        return null
    }

    const extra = count - asins.length
    const links = asins.map((asin) => {
        return <AmazonLink key={asin} asin={asin} />
    })
    if (extra > 0) {
        links.push('and ' + extra + ' others')
    }
    return links.reduce((prev, curr) => [prev, ', ', curr])
}

// Create a list of asins and dedupe them
const getAsins = (item) => {
    const asins = {}

    item?.campaign?.ad_groups?.forEach((adGroup) => {
        adGroup?.product_ads.forEach((productAd) => {
            asins[productAd.asin] = true
        })
    })

    item?.ad_groups?.forEach((adGroup) => {
        adGroup?.product_ads?.forEach((productAd) => {
            asins[productAd.asin] = true
        })
    })

    item?.ad_group?.product_ads?.forEach((productAd) => {
        asins[productAd.asin] = true
    })

    item?.product_ads?.forEach((productAd) => {
        asins[productAd.asin] = true
    })

    return Object.keys(asins)
}
